import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Info } from "lucide-react";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { RelayMark, BrandLockup } from "@/components/brand";
import { Btn, Field, Input, Panel } from "@/components/kit";

export default function DriverAuth() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [reg, setReg] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [uatOtp, setUatOtp] = useState(false);
  const [loading, setLoading] = useState(false);

  const start = async (e) => {
    e?.preventDefault();
    if (!reg.trim() || !mobile.trim()) return;
    setLoading(true);
    try {
      const res = await api.verifyStart(reg, mobile);
      setDevCode(res.dev_code || "");
      setUatOtp(!!res.uat_otp);
      setStep(2);
      toast.success(res.uat_otp ? "Enter your security code to continue." : "Verification code sent (development mode).");
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  const confirm = async (e) => {
    e?.preventDefault();
    if (code.length < 6) return;
    setLoading(true);
    try {
      const res = await api.verifyConfirm(reg, mobile, code);
      await supabase.auth.setSession({
        access_token: res.access_token, refresh_token: res.refresh_token,
      });
      toast.success("Verified. Welcome back.");
      if (res.resolution_required) {
        nav("/driver/allocation-resolve", { replace: true,
          state: { vehicle_id: res.vehicle_id, registration: res.registration || reg } });
      } else {
        nav("/driver/mileage", { replace: true });
      }
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md px-5 py-8 vda-rise">
        <button onClick={() => (step === 2 ? setStep(1) : nav("/"))} className="flex items-center gap-1 text-sm text-muted-foreground mb-8">
          <ChevronLeft className="h-4 w-4" /> {step === 2 ? "Change details" : "Home"}
        </button>

        <div className="mb-8">
          <BrandLockup />
          <div className="mt-3 flex items-baseline gap-2 text-muted-foreground text-sm">
            Fleet driver assistance
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={start} className="space-y-5" data-testid="verify-form">
            <p className="text-sm text-foreground/80">
              Enter your vehicle registration and the mobile number held by your fleet team.
            </p>
            <Field label="Vehicle registration">
              <Input data-testid="input-registration" value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())}
                placeholder="AB12 CDE" className="vda-reg tracking-widest text-lg uppercase" autoCapitalize="characters" />
            </Field>
            <Field label="Mobile number">
              <Input data-testid="input-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)}
                placeholder="07700 900001" inputMode="tel" />
            </Field>
            <Btn data-testid="verify-continue" type="submit" loading={loading} className="w-full">Continue</Btn>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We use these details only to locate your active vehicle allocation. We will never confirm
              whether a registration belongs to a named person. Need help? Contact your fleet team.
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={confirm} className="space-y-5" data-testid="code-form">
            <p className="text-sm text-foreground/80">
              {uatOtp
                ? "Enter your six-digit security code to continue."
                : "Enter the six-digit verification code. In this development build the code is shown below."}
            </p>
            {devCode && (
              <Panel className="p-3 flex items-start gap-2 bg-[hsl(var(--vda-clay))]/8 border-[hsl(var(--vda-clay))]/30" data-testid="dev-code-helper">
                <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--vda-clay))]" />
                <div className="text-sm">
                  <span className="vda-tick text-[hsl(var(--vda-clay))]">Development code</span>
                  <div className="vda-reg text-2xl tracking-[0.3em] text-primary font-semibold">{devCode}</div>
                </div>
              </Panel>
            )}
            {uatOtp && (
              <Panel className="p-3 flex items-start gap-2 bg-[hsl(var(--vda-clay))]/8 border-[hsl(var(--vda-clay))]/30" data-testid="uat-otp-helper">
                <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--vda-clay))]" />
                <div className="text-sm">
                  <span className="vda-tick text-[hsl(var(--vda-clay))]">UAT test build</span>
                  <p className="text-muted-foreground mt-0.5">Use the shared UAT security code provided by your test coordinator.</p>
                </div>
              </Panel>
            )}
            <Field label="Verification code">
              <Input data-testid="input-otp" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" inputMode="numeric" className="vda-reg tracking-[0.5em] text-center text-2xl" />
            </Field>
            <Btn data-testid="verify-confirm" type="submit" loading={loading} className="w-full">Verify &amp; continue</Btn>
            <button type="button" onClick={start} className="w-full text-sm text-muted-foreground hover:text-foreground">
              Resend code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
